import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { api } from "../../api/api";
import { ERROR_MESSAGES, LoadState } from "../../constants/enums";
import {
  IOutlookCalenderEvent,
  IOutlookEmailComposePayload,
  IOutlookEventPayload,
  IOutlookFolder,
  IOutlookInboxEmail,
  IOutlookLabel,
  IOutlookMessageById,
  IOutlookNotification,
  OUTLOOK_MESSAGE_FLAGS,
} from "./outlook.types";
import { IStoreState } from "../initialStoreState";
import { disconnectOutlook } from "./outlookSlice";

export const handleApiError = (error: any, dispatch: any) => {
  if (error?.response?.status === 407) {
    dispatch(disconnectOutlook());
  }
};

export const outlookConnectStatus = createAsyncThunk<
  {
    connected: boolean;
    email: string;
    expiresAt: string;
    isExpired: boolean;
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchConnectStatus", async (_, { rejectWithValue, dispatch }) => {
  try {
    const finalUrl = `outlook/status`;
    const res = await api.get(finalUrl);
    const data = res.data.data.connected;

    return {
      connected: data,
      email: res.data.data.email,
      expiresAt: res.data.data.expiresAt,
      isExpired: res.data.data.isExpired,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const outlookInitiateAuth = createAsyncThunk<
  {
    url: string;
  },
  {
    redirect_uri: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchConnectStatus",
  async ({ redirect_uri }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.get(
        `/outlook/auth/initiate?redirect_uri=${redirect_uri}`,
      );
      const data = res.data.data;

      return {
        url: data.authUrl,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const outlookDisconnect = createAsyncThunk<
  {
    data: string;
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchConnectStatus", async (_, { rejectWithValue, dispatch }) => {
  try {
    await api.post("/outlook/disconnect");

    return {
      data: "Disconnected successfully",
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchOutlookFoldersList = createAsyncThunk<
  {
    folders: IOutlookFolder[];
  },
  {},
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookFoldersList",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const finalUrl = `outlook/folders`;
      const res = await api.get(finalUrl);

      return {
        folders: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookFoldersEmailsList = createAsyncThunk<
  {
    emailList: IOutlookInboxEmail[];
    hasMore: boolean;
  },
  {
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookFoldersEmailsList",
  async ({ pageNumber, pageSize }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { outlook } = getState() as IStoreState;
      let finalUrl = `outlook/folders/${outlook.selectedFolderId}/messages?page=${pageNumber}&pageSize=${pageSize}`;
      if (outlook.emailsMeta.searchQuery) {
        finalUrl = `/outlook/messages/search?q=${encodeURIComponent(outlook.emailsMeta.searchQuery)}&page=${pageNumber}&pageSize=${pageSize}`;
      }
      const res = await api.get(finalUrl);
      const totalRecords = res.data.totalRecords;

      return {
        emailList: res.data.data,
        hasMore: res.data.data.length !== 0,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { outlook } = getState() as IStoreState;
      if (
        outlook.emails.loading === LoadState.InProgress ||
        !outlook.selectedFolderId
      ) {
        return false;
      }
      return true;
    },
  },
);

export const fetchOutlookInboxByMessageId = createAsyncThunk<
  {
    message: IOutlookMessageById;
  },
  {
    messageId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookInboxByMessageId",
  async ({ messageId }, { rejectWithValue, dispatch }) => {
    try {
      const finalUrl = `/outlook/messages/${messageId}`;
      const res = await api.get(finalUrl);

      return {
        message: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookNotifications = createAsyncThunk<
  {
    notifications: IOutlookNotification[];
  },
  {
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookNotifications",
  async ({ pageNumber, pageSize }, { rejectWithValue, dispatch }) => {
    try {
      const finalUrl = `/outlook/notifications?read_status=UNREAD&pageNo=${pageNumber}&itemPerPage=${pageSize}`;
      const res = await api.get(finalUrl);

      return {
        notifications: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const markReadEmailNotification = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    messageId: string;
    userfactId: number;
  },
  { rejectValue: APIError }
>(
  "outlook/markReadEmailNotification",
  async ({ messageId, userfactId }, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/outlook/notifications/read`, {
        message_id: messageId,
        user_fact_id: userfactId,
      });

      return {
        data: "Notification marked as read",
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const markReadUnreadEmailInOutlook = createAsyncThunk<
  {
    data: string;
    messageId: string;
    isRead: boolean;
  },
  {
    messageId: string;
    isRead?: boolean;
  },
  { rejectValue: APIError }
>(
  "outlook/markReadUnreadEmailInOutlook",
  async ({ messageId, isRead = true }, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/outlook/messages/${messageId}/read-status`, {
        isRead: isRead ?? true,
      });
      return {
        data: isRead
          ? "Notification marked as read"
          : "Notification marked as unread",
        messageId,
        isRead,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const sendOutlookEmail = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    payload: IOutlookEmailComposePayload;
  },
  { rejectValue: APIError }
>(
  "outlook/sendOutlookEmail",
  async ({ payload }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/outlook/send`, payload);
      return {
        data: "Email sent successfully",
        messageId: "",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookMessageById = createAsyncThunk<
  {
    messageIds: string[];
  },
  {
    messageIds: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookMessageById",
  async ({ messageIds }, { rejectWithValue, dispatch }) => {
    try {
      const query = messageIds.map((id) => `messageIds=${id}`).join("&");
      await api.delete(`/outlook/messages-v2?${query}`);
      return {
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const createFolder = createAsyncThunk<
  {
    data: string;
  },
  {
    folderName: string;
    parentFolderId?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/createFolder",
  async ({ folderName, parentFolderId }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/outlook/folders`, {
        displayName: folderName,
        parentFolderId: parentFolderId,
      });
      return {
        data: "Folder created successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookFolder = createAsyncThunk<
  {
    folderId: string;
  },
  {
    folderId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookFolder",
  async ({ folderId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/outlook/folders/${folderId}`);
      return {
        folderId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const changeMessageFlag = createAsyncThunk<
  {
    data: string;
    messageId: string;
    flag: OUTLOOK_MESSAGE_FLAGS;
    dueDateTime?: string;
  },
  {
    messageId: string;
    flag: OUTLOOK_MESSAGE_FLAGS;
    dueDateTime?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/changeMessageFlag",
  async ({ messageId, flag, dueDateTime }, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/outlook/messages/${messageId}/flag`, {
        flagStatus: flag,
        dueDateTime: dueDateTime,
      });
      return {
        data: "Flag updated successfully",
        messageId,
        flag,
        dueDateTime,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookLabels = createAsyncThunk<
  {
    labels: IOutlookLabel[];
  },
  {},
  { rejectValue: APIError }
>("outlook/fetchOutlookLabels", async (_, { rejectWithValue, dispatch }) => {
  try {
    const finalUrl = `/outlook/labels`;
    const res = await api.get(finalUrl);

    return {
      labels: res.data.data,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createLabel = createAsyncThunk<
  {
    label: IOutlookLabel;
  },
  {
    labelName: string;
    color: string;
  },
  { rejectValue: APIError }
>(
  "outlook/createLabel",
  async ({ labelName, color }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/outlook/labels`, {
        displayName: labelName,
        color: color,
      });
      return {
        label: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const assignLabelToMessage = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    labels: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/assignLabelToMessage",
  async ({ messageId, labels }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.patch(`/outlook/messages/${messageId}/label`, {
        categories: labels,
      });
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const replyOutlookEmail = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    comment: string;
    replyAll: boolean;
  },
  { rejectValue: APIError }
>(
  "outlook/replyOutlookEmail",
  async ({ messageId, comment, replyAll }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/outlook/messages/${messageId}/reply`, {
        comment,
        replyAll,
      });
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const forwardOutlookEmail = createAsyncThunk<
  {
    data: string[];
    messageId: string;
  },
  {
    messageId: string;
    to: string[];
    cc: string[];
    comment: string;
  },
  { rejectValue: APIError }
>(
  "outlook/forwardOutlookEmail",
  async ({ messageId, to, cc, comment }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/outlook/messages/${messageId}/forward`, {
        to,
        cc,
        comment,
      });
      return {
        data: res.data.data,
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const logoutOutlook = createAsyncThunk<
  {
    data: string;
  },
  {},
  { rejectValue: APIError }
>("outlook/logoutOutlook", async (_, { rejectWithValue, dispatch }) => {
  try {
    await api.post(`/outlook/disconnect`);
    return {
      data: "Outlook disconnected successfully",
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const archiveOutlookMessageById = createAsyncThunk<
  {
    messageIds: string[];
  },
  {
    messageIds: string[];
  },
  { rejectValue: APIError }
>(
  "outlook/archiveOutlookMessageById",
  async ({ messageIds }, { rejectWithValue, dispatch }) => {
    try {
      const query = messageIds.map((id) => `messageIds=${id}`).join("&");
      await api.post(`/outlook/messages/archive-v2?${query}`);
      return {
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const attachPolicyToEmail = createAsyncThunk<
  {
    data: string;
    messageId: string;
  },
  {
    messageId: string;
    customerPolicyId: string;
    email_history_code: string;
    module_name: string;
    status: string;
  },
  { rejectValue: APIError }
>(
  "outlook/attachPolicyToEmail",
  async (
    { messageId, customerPolicyId, email_history_code, module_name, status },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await api.post(`/outlook/upsert-outlook-email-history`, {
        messageId: messageId,
        customer_policy_id: customerPolicyId,
        email_history_code,
        module_name,
        status,
      });
      return {
        data: "Policy attached successfully",
        messageId,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchCalenderEvents = createAsyncThunk<
  {
    data: IOutlookCalenderEvent[];
  },
  {
    startDateTime: string;
    endDateTime: string;
  },
  { rejectValue: APIError }
>("outlook/fetchCalenderEvents", async ({ startDateTime, endDateTime }, { rejectWithValue, dispatch }) => {
  try {
    const res = await api.get(`/outlook/calendar/events?startDateTime=${startDateTime}&endDateTime=${endDateTime}`);
    return {
      data: res.data.data,
    };
  } catch (err: any) {
    handleApiError(err, dispatch);
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const upsertOulookEvent = createAsyncThunk<
  {
    data: string;
  },
  {
    eventData: IOutlookEventPayload;
    eventId?: string;
  },
  { rejectValue: APIError }
>(
  "outlook/upsertOulookEvent",
  async ({ eventData, eventId }, { rejectWithValue, dispatch }) => {
    try {
      if (eventId) {
        await api.patch(`/outlook/calendar/events/${eventId}`, eventData);
      } else {
        await api.post(`/outlook/calendar/events`, eventData);
      }
      return {
        data: "Event created successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchOutlookEventById = createAsyncThunk<
  {
    data: IOutlookCalenderEvent;
  },
  {
    eventId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/fetchOutlookEventById",
  async ({ eventId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.get(`/outlook/calendar/events/${eventId}`);

      return {
        data: res.data.data,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteOutlookEvent = createAsyncThunk<
  {
    data: string;
  },
  {
    eventId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/deleteOutlookEvent",
  async ({ eventId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/outlook/calendar/events/${eventId}`);
      return {
        data: "Event deleted successfully",
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);


export const moveOutlookMessagesToFolder = createAsyncThunk<
  { folderId: string; messageIds: string[] },
  { folderId: string; messageIds: string[] },
  { rejectValue: APIError }
>(
  "outlook/moveOutlookMessagesToFolder",
  async ({ folderId, messageIds }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/outlook/messages/move`, { folderId, messageIds });
      return {
        folderId,
        messageIds,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const batchMarkReadUnreadInOutlook = createAsyncThunk<
  { messageIds: string[]; isRead: boolean },
  { messageIds: string[]; isRead: boolean },
  { rejectValue: APIError }
>(
  "outlook/batchMarkReadUnreadInOutlook",
  async ({ messageIds, isRead }, { rejectWithValue, dispatch }) => {
    try {
      await Promise.all(
        messageIds.map((messageId) =>
          api.patch(`/outlook/messages/${messageId}/read-status`, { isRead }),
        ),
      );
      return {
        messageIds,
        isRead,
      };
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const downloadOutlookEmail = createAsyncThunk<
  {
    data: IOutlookCalenderEvent;
  },
  {
    messageId: string;
  },
  { rejectValue: APIError }
>(
  "outlook/downloadOutlookEmail",
  async ({ messageId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.get(`/outlook/messages/${messageId}/raw`);
      return res;
    } catch (err: any) {
      handleApiError(err, dispatch);
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
